import type {
  BorderRadius,
  Padding,
  TooltipOverlayProps,
} from '@shopify/polaris/build/ts/src/components/Tooltip';
import type { GTableHeaderProps, GTooltipProps } from '~/presentation/ui';

import {
  GTextLink,
  GTooltip,
  useGTableContext,
  GTable,
  GTooltipCard,
  GStopPropagation,
  GTextField,
} from '~/presentation/ui';
import { Box, Button, Icon, InlineGrid, InlineStack, Text } from '@shopify/polaris';
import { EditIcon, InfoIcon } from '@shopify/polaris-icons';
import { useToggle } from '~/hooks/useToggle';
import { useState } from 'react';

export const Header = () => {
  const [newTitle, setNewTitle] = useState('');

  const { listHeader, alignments, parsePaddingRow } = useGTableContext();
  const { setFalse, setTrue, value } = useToggle(false);
  const { setFalse: setOffRename, setTrue: setOnRenaming, value: isRenaming } = useToggle(false);

  const onRename = (title: string) => {
    setNewTitle(title);
    setOnRenaming();
  };
  const handleRename = async (header: GTableHeaderProps) => {
    setOffRename();
    await header.rename?.onAction?.(newTitle.trim());
  };

  const renderHeaderContent = (header: GTableHeaderProps, index: number) => {
    const headingAlignment = alignments[index] ?? 'start';

    const headerTooltipProps: GTooltipProps = {
      activatorWrapper: 'div',
      dismissOnMouseOut: true,
      padding: '200' as Padding,
      borderRadius: '200' as BorderRadius,
      persistOnClick: header.tooltipPersistsOnClick,
      preferredPosition: 'above' as TooltipOverlayProps['preferredPosition'],
      width: header.tooltipWidth ?? 'wide',
      content: header.tooltipContent,
    };

    const headingTitle = (
      <div className="overflow-hidden">
        <GTextLink linkAction={header.linkAction} isDisabled={header.isStopPropagation}>
          <GTooltip content={header.title} width="wide" isEnabled={!!header.text?.isEnabledTooltip}>
            <Text alignment={headingAlignment} variant="bodySm" as="p" {...header.text}>
              {header.title}
            </Text>
          </GTooltip>
        </GTextLink>
      </div>
    );

    const headingTitleRename = (
      <div className="overflow-hidden">
        <InlineGrid
          columns="100%"
          {...((value || header.rename?.loading) && { columns: 'calc(100% - 36px) 28px' })}
          gap={'200'}
          alignItems="center"
        >
          <>
            <GTooltip
              content={header.title}
              width="wide"
              isEnabled={!!header.text?.isEnabledTooltip}
            >
              <Text alignment={headingAlignment} variant="bodySm" as="p" {...header.text}>
                {header.title}
              </Text>
            </GTooltip>
            {(value || header.rename?.loading) && (
              <GTooltip content={'Rename'} isEnabled={true}>
                <div className="w-[28px] h-[28px] flex items-center justify-center">
                  <Button
                    size="slim"
                    loading={header.rename?.loading}
                    variant="tertiary"
                    icon={EditIcon}
                    onClick={() => onRename(header.title as string)}
                  ></Button>
                </div>
              </GTooltip>
            )}
          </>
        </InlineGrid>
      </div>
    );

    const HeaderContent = header.tooltipCard ? (
      <>
        <GTooltipCard tooltip={header.tooltipCard}>
          <Text alignment={headingAlignment} variant="bodySm" as="p" {...header.text}>
            {header.title}
          </Text>
        </GTooltipCard>
      </>
    ) : (
      <InlineStack align={headingAlignment} blockAlign="center" gap="200" wrap={false}>
        {header.rename ? headingTitleRename : headingTitle}

        {header.tooltipContent && (
          <GTooltip {...headerTooltipProps}>
            <Icon source={InfoIcon}></Icon>
          </GTooltip>
        )}
      </InlineStack>
    );

    return (
      <Box maxWidth="100%" width="100%">
        {HeaderContent}
      </Box>
    );
  };

  const renderHeader = (header: GTableHeaderProps, index: number, id?: string) => {
    const paddingBox = parsePaddingRow(index);

    if (header.isFlushLeft) return;
    return (
      <GStopPropagation key={id} isDisabled={!header.isStopPropagation}>
        <div className="h-full" onMouseEnter={setTrue} onMouseLeave={setFalse}>
          {isRenaming && header.rename ? (
            <Box {...paddingBox}>
              <div className="w-full pl-1">
                <GTextField
                  size="slim"
                  focused={true}
                  maxLength={header.rename?.maxLength || 255}
                  error={header.rename?.error || ''}
                  placeholder={header.rename?.placeholder || ''}
                  value={newTitle}
                  onChange={(value) => setNewTitle(value)}
                  onBlur={() => handleRename(header)}
                  onEnter={() => handleRename(header)}
                />
              </div>
            </Box>
          ) : (
            <GTable.Cell key={`${index}-cell`} index={index} padding={paddingBox}>
              <div id={id} style={{ maxWidth: '100%' }}>
                {renderHeaderContent(header, index)}
              </div>
            </GTable.Cell>
          )}
        </div>
      </GStopPropagation>
    );
  };

  return listHeader.map((header, index) => renderHeader(header, index, header.id));
};
